import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { adminAPI } from '../../../api/bookings';

/**
 * Forgot-password flow state machine:
 *   'email'    → user enters email, we POST /admin/forgot-password
 *   'otp'      → user enters 5-digit OTP, we POST /admin/verify-reset-otp
 *   'password' → user sets a new password, we POST /admin/reset-password
 * After success → toast + redirect to /admin/login.
 */
export function useAdminForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [otp, setOtp] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendCount, setResendCount] = useState(0);

  useEffect(() => {
    if (resendCooldown > 0) {
      const t = setTimeout(() => setResendCooldown((v) => v - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendCooldown]);

  const submitEmail = useCallback(async (e) => {
    e?.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      const res = await adminAPI.requestPasswordReset(email.trim().toLowerCase());
      // Backend returns `reset_token: null` for unknown emails (generic success).
      // Per enumeration-protection rule we still advance to OTP step so attackers
      // can't tell which emails exist.
      if (res.reset_token) {
        setResetToken(res.reset_token);
      }
      setStep('otp');
      toast.success(res.message || 'If the email is registered, a code has been sent.');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Request failed';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  }, [email]);

  const submitOtp = useCallback(async (e) => {
    e?.preventDefault();
    if (otp.length !== 5) {
      toast.error('Enter the 5-digit code');
      return;
    }
    if (!resetToken) {
      // Generic-success path: we never got a real token. Tell the user but
      // don't reveal enumeration.
      toast.error('Invalid or expired code. Please start again.');
      setStep('email');
      return;
    }
    setLoading(true);
    try {
      const res = await adminAPI.verifyResetOtp(resetToken, otp);
      setAuthToken(res.reset_authorization_token);
      setStep('password');
      toast.success('Verified. Please set a new password.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid verification code');
      setOtp('');
    } finally {
      setLoading(false);
    }
  }, [otp, resetToken]);

  const resendOtp = useCallback(async () => {
    if (resendCooldown > 0 || !resetToken) return;
    setLoading(true);
    try {
      const res = await adminAPI.resendResetOtp(resetToken);
      setResendCount(res.resend_count || 0);
      setResendCooldown(60);
      setOtp('');
      toast.success('New verification code sent!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend code');
      if (err.response?.status === 401) {
        setStep('email');
        setResetToken('');
      }
    } finally {
      setLoading(false);
    }
  }, [resetToken, resendCooldown]);

  const submitNewPassword = useCallback(async (e) => {
    e?.preventDefault();
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await adminAPI.resetPassword(authToken, newPassword);
      toast.success('Password reset successful. Please log in.');
      // Full redirect — fresh /admin/login page, no auto-login by design.
      navigate('/admin/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
      // If the auth_token is dead (replay or expired), kick back to start
      if (err.response?.status === 401) {
        setStep('email');
        setResetToken('');
        setAuthToken('');
        setOtp('');
      }
    } finally {
      setLoading(false);
    }
  }, [newPassword, confirmPassword, authToken, navigate]);

  const backToLogin = useCallback(() => navigate('/admin/login'), [navigate]);

  return {
    step, loading,
    email, setEmail,
    otp, setOtp,
    newPassword, setNewPassword,
    confirmPassword, setConfirmPassword,
    resendCooldown, resendCount,
    submitEmail, submitOtp, resendOtp, submitNewPassword,
    backToLogin,
  };
}
